import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  buildRealtyHistoryUrl,
  buildRealtySortTransition,
  clearRealtyFilter,
  constrainRealtyFilterCapabilities,
  DEFAULT_REALTY_FILTER,
  mergeRealtyFilterSearchParams,
  parseRealtySearchParams,
  realtyFiltersEqual,
} from '../utils/realty-filter-contract';
import type { RealtyFilter, RealtyOrder, RealtyOrderBy } from '../utils/realty-filter-contract';

export type { RealtyFilter } from '../utils/realty-filter-contract';

// Define the store state
type RealtyStoreState = {
  filter: RealtyFilter;
  appliedFilter: RealtyFilter;
  hasHydratedSearchParams: boolean;
  supportsUnderConstruction: boolean;
  autoUpdateSearchParams: boolean;
  clearTrigger: boolean;
  SORT_VALUES: { label: string; value: string }[];
};

// Define the store actions
type RealtyStoreActions = {
  clearFilters: () => void;
  setFilter: (filter: Partial<RealtyFilter>) => void;
  replaceFilter: (filter: RealtyFilter) => void;
  setAutoUpdateSearchParams: (value: boolean) => void;
  applyFilter: (filter: RealtyFilter, historyMode?: 'push' | 'replace') => void;
  applySort: (orderBy: RealtyOrderBy, order: RealtyOrder) => void;
};

// Create the context store
const [RealtyStoreProvider, useRealtyStore] = createContextStore<
  RealtyStoreState,
  RealtyStoreActions
>(
  {
    filter: { ...DEFAULT_REALTY_FILTER },
    appliedFilter: { ...DEFAULT_REALTY_FILTER },
    hasHydratedSearchParams: false,
    supportsUnderConstruction: false,
    autoUpdateSearchParams: true,
    clearTrigger: false,
    SORT_VALUES: [
      { label: 'text179', value: 'id:desc' },
      { label: 'text177', value: 'price:asc' },
      { label: 'text178', value: 'price:desc' },
    ],
  },
  (set, get) => ({
    clearFilters: () => {
      set(state => {
        state.filter = clearRealtyFilter(state.filter);
        state.clearTrigger = !state.clearTrigger;
      });
    },
    setFilter: filter => {
      set(state => {
        state.filter = {
          ...state.filter,
          ...filter,
        };
      });
    },
    replaceFilter: filter => {
      const normalized = constrainRealtyFilterCapabilities(filter, {
        underConstruction: get().supportsUnderConstruction,
      });

      set(state => {
        const alreadyApplied = realtyFiltersEqual(state.appliedFilter, normalized);
        state.appliedFilter = normalized;
        state.hasHydratedSearchParams = true;

        // Own URL writes set appliedFilter before history.replaceState. Keeping
        // the current draft here prevents a sort change from submitting or
        // discarding unsubmitted filter controls. External URL navigation still
        // replaces the draft with the newly applied filter.
        if (!alreadyApplied) state.filter = normalized;
      });
    },
    setAutoUpdateSearchParams: value => {
      set(state => {
        state.autoUpdateSearchParams = value;
      });
    },
    applyFilter: (filter: RealtyFilter, historyMode = 'push') => {
      const normalized = constrainRealtyFilterCapabilities(filter, {
        underConstruction: get().supportsUnderConstruction,
      });

      set(state => {
        state.filter = normalized;
        state.appliedFilter = normalized;
        state.hasHydratedSearchParams = true;
      });

      if (typeof window === 'undefined') return;

      const pathname = window.location.pathname;
      const hash = window.location.hash;
      const searchString = mergeRealtyFilterSearchParams(window.location.search, normalized);
      const nextUrl = buildRealtyHistoryUrl(pathname, searchString, hash);
      const currentUrl = buildRealtyHistoryUrl(pathname, window.location.search, hash);

      if (nextUrl !== currentUrl) {
        if (historyMode === 'replace') window.history.replaceState({}, '', nextUrl);
        else window.history.pushState({}, '', nextUrl);
      }
    },
    applySort: (orderBy, order) => {
      const currentApplied =
        typeof window === 'undefined'
          ? get().appliedFilter
          : parseRealtySearchParams(new URLSearchParams(window.location.search));
      const draft = constrainRealtyFilterCapabilities(get().filter, {
        underConstruction: get().supportsUnderConstruction,
      });
      const applied = constrainRealtyFilterCapabilities(currentApplied, {
        underConstruction: get().supportsUnderConstruction,
      });
      const next = buildRealtySortTransition(draft, applied, orderBy, order);

      set(state => {
        state.filter = next.draftFilter;
        state.appliedFilter = next.appliedFilter;
        state.hasHydratedSearchParams = true;
      });

      if (typeof window === 'undefined') return;

      const pathname = window.location.pathname;
      const hash = window.location.hash;
      const searchString = mergeRealtyFilterSearchParams(
        window.location.search,
        next.appliedFilter,
      );
      const nextUrl = buildRealtyHistoryUrl(pathname, searchString, hash);
      const currentUrl = buildRealtyHistoryUrl(pathname, window.location.search, hash);

      if (nextUrl !== currentUrl) window.history.pushState({}, '', nextUrl);
    },
  }),
);

// RealtyFilterParamsSync component
function RealtyFilterParamsSync() {
  const filter = useRealtyStore(state => state.filter);
  const appliedFilter = useRealtyStore(state => state.appliedFilter);
  const hasHydratedSearchParams = useRealtyStore(state => state.hasHydratedSearchParams);
  const autoUpdateSearchParams = useRealtyStore(state => state.autoUpdateSearchParams);
  const replaceFilter = useRealtyStore(state => state.replaceFilter);
  const applyFilter = useRealtyStore(state => state.applyFilter);
  const searchParams = useSearchParams();
  const supportsUnderConstruction = useRealtyStore(state => state.supportsUnderConstruction);

  useEffect(() => {
    const parsedFilter = parseRealtySearchParams(searchParams);
    const appliedFilter = constrainRealtyFilterCapabilities(parsedFilter, {
      underConstruction: supportsUnderConstruction,
    });
    replaceFilter(appliedFilter);

    if (!realtyFiltersEqual(parsedFilter, appliedFilter)) {
      const pathname = window.location.pathname;
      const hash = window.location.hash;
      const searchString = mergeRealtyFilterSearchParams(searchParams.toString(), appliedFilter);
      const nextUrl = buildRealtyHistoryUrl(pathname, searchString, hash);
      const currentUrl = buildRealtyHistoryUrl(pathname, window.location.search, hash);

      if (nextUrl !== currentUrl) window.history.replaceState({}, '', nextUrl);
    }
  }, [replaceFilter, searchParams, supportsUnderConstruction]);

  useEffect(() => {
    if (!autoUpdateSearchParams) return;

    if (!hasHydratedSearchParams) return;
    if (!realtyFiltersEqual(filter, appliedFilter)) applyFilter(filter, 'replace');
  }, [appliedFilter, applyFilter, autoUpdateSearchParams, filter, hasHydratedSearchParams]);

  return null;
}

// Export the components
export { RealtyStoreProvider, useRealtyStore, RealtyFilterParamsSync };
